import { useState } from 'react';
import { AlertBanner, Button } from '@insio/ui-toolkit';
import { CreditUsageLevel } from '@insio/shared-types';
import type { CreditUsageFieldsFragment } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useCreditPackOffer } from '../hooks/use-credit-pack-offer';
import { CreditCallToActionKind, creditCallToAction } from '../utils/credit-call-to-action';
import { BuyCreditPackModal } from './BuyCreditPackModal';

interface CreditUsageCallToActionProps {
  /** The payer's usage; renders nothing without figures or at `OK`. */
  usage: CreditUsageFieldsFragment;
  /** Shows the plans to move to; `null` when the page has none. */
  onUpgrade: (() => void) | null;
  /** Opens the billing portal; `null` without a Stripe subscription. */
  onOpenBillingPortal: (() => void) | null;
  /** True while the billing portal session is being opened. */
  billingPortalOpening?: boolean;
}

/** From `LOW`, what the payer can do about its credits, as a full-width strip with a top border. */
export function CreditUsageCallToAction({
  usage,
  onUpgrade,
  onOpenBillingPortal,
  billingPortalOpening = false
}: CreditUsageCallToActionProps) {
  const t = useFeatureTranslation('credits.billing');
  const tPacks = useFeatureTranslation('credits.packs');
  const [buyOpen, setBuyOpen] = useState(false);

  const figures = usage.figures;
  const actionable = figures != null && figures.level !== CreditUsageLevel.OK;
  const { offer } = useCreditPackOffer({ skip: !actionable });

  if (!figures || !actionable) return null;

  const action = creditCallToAction({
    level: figures.level,
    offer,
    canUpgrade: onUpgrade !== null,
    canOpenBillingPortal: onOpenBillingPortal !== null
  });

  return (
    <>
      <div className='border-t border-border-base px-md py-sm'>
        <AlertBanner
          variant={figures.level === CreditUsageLevel.EXHAUSTED ? 'error' : 'warning'}
          size='sm'
          testId='credit-usage-cta'
          title={t(figures.level === CreditUsageLevel.EXHAUSTED ? 'exhausted' : 'low')}
          body={action.refusal ? tPacks(`refusal.${action.refusal}`) : undefined}
        >
          <CallToActionButton
            kind={action.kind}
            onBuy={() => setBuyOpen(true)}
            onUpgrade={onUpgrade}
            onOpenBillingPortal={onOpenBillingPortal}
            billingPortalOpening={billingPortalOpening}
          />
        </AlertBanner>
      </div>

      <BuyCreditPackModal open={buyOpen} onClose={() => setBuyOpen(false)} />
    </>
  );
}

interface CallToActionButtonProps {
  kind: CreditCallToActionKind;
  onBuy: () => void;
  onUpgrade: (() => void) | null;
  onOpenBillingPortal: (() => void) | null;
  billingPortalOpening: boolean;
}

function CallToActionButton({
  kind,
  onBuy,
  onUpgrade,
  onOpenBillingPortal,
  billingPortalOpening
}: CallToActionButtonProps) {
  const t = useFeatureTranslation('credits.billing');
  const className = 'mt-xs self-start';

  switch (kind) {
    case CreditCallToActionKind.BUY_PACK:
    case CreditCallToActionKind.PACKS_DISABLED:
      return (
        <Button
          variant='primary'
          size='sm'
          className={className}
          disabled={kind === CreditCallToActionKind.PACKS_DISABLED}
          onClick={onBuy}
        >
          {t('buyPack')}
        </Button>
      );
    case CreditCallToActionKind.UPGRADE:
      return onUpgrade ? (
        <Button variant='primary' size='sm' className={className} onClick={onUpgrade}>
          {t('upgrade')}
        </Button>
      ) : null;
    case CreditCallToActionKind.BILLING_PORTAL:
      return onOpenBillingPortal ? (
        <Button
          variant='primary'
          size='sm'
          className={className}
          disabled={billingPortalOpening}
          onClick={onOpenBillingPortal}
        >
          {t('openPortal')}
        </Button>
      ) : null;
    default:
      return null;
  }
}
